import { useEffect, useState } from "react";
import supabase from "@/supabase";
import type { BotSummary, BotPersona } from "@/types/bot";

interface UseBotsResult {
  bots: BotSummary[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useBots(): UseBotsResult {
  const [bots, setBots] = useState<BotSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBots = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("bots")
        .select("id, name, avatar_url, persona")
        .order("created_at", { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      const botSummaries: BotSummary[] = (data || []).map((bot) => {
        const persona = bot.persona as BotPersona;
        return {
          id: bot.id,
          name: bot.name,
          avatar_url: bot.avatar_url,
          description: persona?.description || "",
        };
      });

      setBots(botSummaries);
    } catch (err) {
      console.error("Error fetching bots:", err);
      setError("Failed to load bots. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  return {
    bots,
    isLoading,
    error,
    refetch: fetchBots,
  };
}
